/**
 * MagicBI Design System - Border Tokens
 * 
 * Border radius, width, and style tokens for consistent
 * component styling and interface elements.
 */

// Border radius values
export const borderRadius = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  '2xl': '16px',
  '3xl': '24px',
  full: '9999px', // For circular elements

  // Exact pixel radii (story redesign), keyed by px value, written in rem
  3: '0.1875rem', // 3px
  5: '0.3125rem', // 5px
  7: '0.4375rem', // 7px
  9: '0.5625rem', // 9px
  10: '0.625rem', // 10px
  14: '0.875rem', // 14px
  18: '1.125rem', // 18px
  20: '1.25rem', // 20px
  22: '1.375rem', // 22px
  28: '1.75rem', // 28px
} as const;

// Border widths
export const borderWidth = {
  0: '0',
  /** Figma KG nodes — 0.8px hairline strokes */
  hairline: '0.8px',
  1: '1px',
  2: '2px',
  4: '4px',
  8: '8px',
} as const;

// Border styles
export const borderStyle = {
  none: 'none',
  solid: 'solid',
  dashed: 'dashed',
  dotted: 'dotted',
} as const;

// Semantic border radius tokens
export const semanticBorderRadius = {
  // Component-specific radius
  button: {
    sm: borderRadius.sm,
    md: borderRadius.md,
    lg: borderRadius.lg,
    pill: borderRadius.full,
  },
  
  input: {
    default: borderRadius.md,
    sm: borderRadius.sm,
    lg: borderRadius.lg,
  },
  
  card: {
    default: borderRadius.lg,
    sm: borderRadius.md,
    lg: borderRadius.xl,
    /** AI summary / gradient-border cards — Figma 4306:346315 (10px sits between the lg/xl scale steps) */
    ai: '10px',
  },
  
  modal: {
    default: borderRadius.xl,
    lg: borderRadius['2xl'],
  },
  
  avatar: {
    default: borderRadius.full,
    square: borderRadius.md,
  },
  
  badge: {
    default: borderRadius.full,
    square: borderRadius.sm,
  },
  
  tooltip: borderRadius.md,
  popover: borderRadius.lg,
  dropdown: borderRadius.lg,
  
  // Special cases
  chip: borderRadius.full,
  tag: borderRadius.sm,
  image: borderRadius.md,
} as const;

// Component border configurations
export const componentBorders = {
  // Default border styles for components
  button: {
    primary: {
      width: borderWidth[1],
      style: borderStyle.solid,
      radius: semanticBorderRadius.button.md,
    },
    secondary: {
      width: borderWidth[1],
      style: borderStyle.solid,
      radius: semanticBorderRadius.button.md,
    },
    tertiary: {
      width: borderWidth[0],
      style: borderStyle.none,
      radius: semanticBorderRadius.button.md,
    },
  },
  
  input: {
    default: {
      width: borderWidth[1],
      style: borderStyle.solid,
      radius: semanticBorderRadius.input.default,
    },
    focus: {
      width: borderWidth[2],
      style: borderStyle.solid,
      radius: semanticBorderRadius.input.default,
    },
    error: {
      width: borderWidth[1],
      style: borderStyle.solid,
      radius: semanticBorderRadius.input.default,
    },
  },
  
  card: {
    default: {
      width: borderWidth[1],
      style: borderStyle.solid,
      radius: semanticBorderRadius.card.default,
    },
    elevated: {
      width: borderWidth[0],
      style: borderStyle.none,
      radius: semanticBorderRadius.card.default,
    },
  },
  
  modal: {
    width: borderWidth[0],
    style: borderStyle.none,
    radius: semanticBorderRadius.modal.default,
  },
  
  dropdown: {
    width: borderWidth[1],
    style: borderStyle.solid,
    radius: semanticBorderRadius.dropdown,
  },
  
  divider: {
    width: borderWidth[1],
    style: borderStyle.solid,
    radius: borderRadius.none,
  },
} as const;

// Border color tokens (semantic)
export const borderColors = {
  // Light theme
  light: {
    default: '#E6E1F3',      // structural.grey200
    subtle: '#F0F6FF',       // structural.grey100
    strong: '#AFADBA',       // grey.500
    inverse: '#191236',      // grey.900
    
    // Interactive states
    hover: '#C6D0E1',        // stroke.300
    focus: '#6442B3',        // brand.primary
    active: '#4B3286',       // brand.primaryDark
    disabled: '#DEDCE5',     // grey.400
    
    // Status colors
    success: '#1BBE65',      // accent.green
    warning: '#FB975B',      // accent.peach
    error: '#FF0A80',        // accent.pink
    info: '#1CAEC8',         // accent.turquoise
  },
  
  // Dark theme
  dark: {
    default: '#726F80',      // grey.600
    subtle: '#AFADBA',       // grey.500
    strong: '#E4EBF5',       // structural.grey200
    inverse: '#FFFFFF',      // structural.white
    
    // Interactive states
    hover: '#8D9DBD',        // Adjusted for dark theme
    focus: '#AA97DB',        // brand.primaryLight
    active: '#C6D0E1',       // Adjusted for dark theme
    disabled: '#566682',     // Adjusted for dark theme
    
    // Status colors
    success: '#1BBE65',      // accent.green
    warning: '#FB975B',      // accent.peach
    error: '#FF0A80',        // accent.pink
    info: '#1CAEC8',         // accent.turquoise
  },
} as const;

// Type definitions
export type BorderRadius = typeof borderRadius;
export type BorderWidth = typeof borderWidth;
export type BorderStyle = typeof borderStyle;
export type SemanticBorderRadius = typeof semanticBorderRadius;
export type ComponentBorders = typeof componentBorders;
export type BorderColors = typeof borderColors;

// Utility functions
export const getBorderRadius = (size: keyof BorderRadius) => borderRadius[size];

export const getSemanticBorderRadius = <T extends keyof SemanticBorderRadius>(
  component: T,
  variant?: keyof SemanticBorderRadius[T]
) => {
  const componentRadius = semanticBorderRadius[component];
  if (variant && typeof componentRadius === 'object') {
    return (componentRadius as any)[variant];
  }
  return componentRadius;
};

export const getComponentBorder = <T extends keyof ComponentBorders>(
  component: T,
  variant?: keyof ComponentBorders[T]
) => {
  const componentBorder = componentBorders[component];
  if (variant && typeof componentBorder === 'object') {
    return (componentBorder as any)[variant];
  }
  return componentBorder;
};

// CSS helper for styled-components
export const borderMixin = {
  radius: (size: keyof BorderRadius) => `
    border-radius: ${borderRadius[size]};
  `,
  
  width: (width: keyof BorderWidth) => `
    border-width: ${borderWidth[width]};
  `,
  
  style: (style: keyof BorderStyle) => `
    border-style: ${borderStyle[style]};
  `,
  
  full: (width: keyof BorderWidth, style: keyof BorderStyle, color: string) => `
    border: ${borderWidth[width]} ${borderStyle[style]} ${color};
  `,
  
  component: <T extends keyof ComponentBorders>(
    component: T,
    variant?: keyof ComponentBorders[T],
    color?: string
  ) => {
    const border = getComponentBorder(component, variant);
    const colorValue = color || 'currentColor';
    return `
      border: ${border.width} ${border.style} ${colorValue};
      border-radius: ${border.radius};
    `;
  },
};
